import { Alert, Flex, Spin, Typography } from "antd";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { CustomButton, TxButton, WhiteBoxBlock } from "ui";
import { ItemInfoBlock } from "components";
import { useStores } from "stores";
import {
  COMPANY_STATUS_ENUM,
  FILE_TYPE_ENUM,
  ExpandNetworkType,
  ItemInfoType,
  isCompanyActive,
  COMPANY_ROLE_ENUM,
  NETWORK_TEXT_WITHOUT_STANDARD,
} from "interfaces";
import { routes } from "constants/routes";
import { CompanyHeader } from "./CompanyHeader";
import { CompanyContractList } from "./CompanyContractList";
import { CompanyUserList } from "./CompanyUserList";
import { getCompanyInfo } from "./getColumns";
import { FeeReportButton, RequestReportButton } from "./Reports";
import { CompanyActivateButton } from "./CompanyActivate";
import { BlockCompanyButton } from "./BlockCompany";
import { UnblockCompanyButton } from "./UnblockCompany";
import { EXPLORER_LINK_TYPE, fileExistsInObject, getBlockchainExplorerLink, parseErrorMessage } from "utils";
import { getStatusMessage } from "services";
import { CompanyFilesBlock } from "./CompanyFilesBlock";
import { BankDetails } from "components/BankDetails/BankDetails";
import { AddUserButton } from "components/AddUser";
import { CreateBeneficiaryButton } from "./CreateBeneficiaryBtn/CreateBeneficiaryButton";
import { DIRECT_PAYMENT_CUSTOMER_LP, SAFE_DEAL } from "utils/envConstants";

export const CompanyInfoPage = observer(() => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { companyCard, companyContracts, companyUsers, user } = useStores();

  const { isUserAdmin, isSeniorOperator, isRiskManager, isJuniorOperator } = user.roles;

  useEffect(() => {
    if (id) {
      companyCard.fetchCompanyCardById(id);
      companyContracts.setId(id);
      companyUsers.setId(id);
    }
  }, [id, companyCard, companyContracts, companyUsers]);

  const { status, wallets, documents, role } = companyCard.card;
  const showFilesBlock = fileExistsInObject(documents, FILE_TYPE_ENUM.OFFER_SIGNED);

  const getWalletBlocks = (): ItemInfoType[] => {
    if (!wallets || wallets.length === 0) return [];

    const walletsByNetwork: Record<string, string> = {};

    wallets.forEach((wallet) => {
      if (wallet.network && wallet.address) {
        walletsByNetwork[wallet.network] = wallet.address;
      }
    });

    return Object.entries(NETWORK_TEXT_WITHOUT_STANDARD)
      .filter(([network]) => walletsByNetwork[network])
      .map(([network, networkName]) => ({
        label: networkName,
        text: (
          <TxButton
            isWalletAddress
            text={walletsByNetwork[network]}
            showCopyBtn
            href={getBlockchainExplorerLink(
              network as ExpandNetworkType,
              walletsByNetwork[network],
              EXPLORER_LINK_TYPE.address
            )}
            infoBlock
          />
        ),
      }));
  };

  const showActivateCompanyButton = status === COMPANY_STATUS_ENUM.DRAFT && (isRiskManager || isUserAdmin);

  const isSafeDealMode =
    SAFE_DEAL && companyCard.card.beneficiaryCreated && companyCard.card.role === COMPANY_ROLE_ENUM.OPERATOR;

  const showEditCompanyBtn = isUserAdmin && !isSafeDealMode;

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  return (
    <Spin spinning={companyCard.isPending}>
      {companyCard.fetchStatus === "success" && (
        <Flex vertical gap="large">
          <CompanyHeader data={companyCard.card} user={user} />

          <Flex gap="middle">
            {showActivateCompanyButton && <CompanyActivateButton />}
            {isCompanyActive(status) && (
              <>
                {isUserAdmin && <AddUserButton />}

                {role === COMPANY_ROLE_ENUM.CUSTOMER && isSeniorOperator && (
                  <CustomButton
                    onClick={() => {
                      navigate(`/${routes.contracts}/create?companyId=${id}`);
                    }}
                  >
                    Новый договор
                  </CustomButton>
                )}

                {showEditCompanyBtn && (
                  <CustomButton
                    onClick={() => {
                      navigate(`/${routes.companies}/edit/${id}`);
                    }}
                  >
                    Редактировать
                  </CustomButton>
                )}
              </>
            )}

            <Flex className="margin-left-auto" gap="middle" wrap="wrap" justify="end">
              {(isRiskManager || isUserAdmin) && (
                <>
                  {status === COMPANY_STATUS_ENUM.BLOCKED && <UnblockCompanyButton />}

                  {status === COMPANY_STATUS_ENUM.ACTIVE && <BlockCompanyButton />}
                </>
              )}

              {(isSeniorOperator || isRiskManager || isJuniorOperator) && (
                <>
                  {companyCard.card.role === COMPANY_ROLE_ENUM.CUSTOMER && (
                    <Flex gap="middle">
                      <RequestReportButton />
                    </Flex>
                  )}
                  {companyCard.card.role === COMPANY_ROLE_ENUM.LIQUIDITY_PROVIDER && (
                    <Flex gap="middle">
                      <FeeReportButton />
                    </Flex>
                  )}
                </>
              )}
            </Flex>
          </Flex>
          {status === COMPANY_STATUS_ENUM.DRAFT && showFilesBlock && <CompanyFilesBlock />}
          <WhiteBoxBlock>
            <ItemInfoBlock blockTitle="Сведения о компании" items={getCompanyInfo(companyCard.card)} />
          </WhiteBoxBlock>
          <WhiteBoxBlock>
            <Flex vertical gap="large">
              <Typography.Text strong className="whitebox-title">
                Банковские реквизиты компании
              </Typography.Text>
              <BankDetails />
            </Flex>
          </WhiteBoxBlock>
          {status !== COMPANY_STATUS_ENUM.DRAFT && showFilesBlock && <CompanyFilesBlock />}
          {role === COMPANY_ROLE_ENUM.LIQUIDITY_PROVIDER && wallets && wallets.length > 0 && (
            <WhiteBoxBlock>
              <ItemInfoBlock blockTitle="Кошельки в блокчейн" items={getWalletBlocks()} />
            </WhiteBoxBlock>
          )}
          <CompanyUserList />
          {role === COMPANY_ROLE_ENUM.CUSTOMER && (isSeniorOperator || isRiskManager || isJuniorOperator) && (
            <CompanyContractList />
          )}
        </Flex>
      )}
      {companyCard.fetchStatus === "error" && companyCard.fetchError && (
        <Alert
          message={getStatusMessage(companyCard.fetchError.status)}
          description={parseErrorMessage(companyCard.fetchError)}
          type="error"
          showIcon
        />
      )}
    </Spin>
  );
});
